import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AlertTriangle, Loader2, Lock } from 'lucide-react';
import { API } from '../../App';
import { LegalFormPicker } from './LegalFormPicker';
import { PartnersEditor, emptyPartner } from './PartnersEditor';

export const LegalFormChangeDialog = ({ open, onOpenChange, current, onChanged }) => {
  const [form, setForm] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [partners, setPartners] = useState([emptyPartner(), emptyPartner()]);
  const [saving, setSaving] = useState(false);

  const options = current?.options;
  const target = options?.forms?.find((f) => f.code === form);
  const currentRegime = current?.regime;
  const regimeChanges = target && currentRegime && target.regime !== currentRegime;

  const submit = async () => {
    if (!form) return toast.error('Choose the new legal form');
    if (!password) return toast.error('Enter your password to confirm');
    setSaving(true);
    try {
      const body = { legal_form: form, effective_from: effectiveFrom, password, reason: reason || null };
      if (target?.partners_required) body.partners = partners.map((p) => ({ ...p, profit_share_pct: parseFloat(p.profit_share_pct) || 0, email: p.email || null }));
      const r = await axios.put(`${API}/business/legal-form/change`, body);
      toast.success(`Legal form changed to ${r.data.legal_form_label}`);
      if (r.data.equity_accounts_created?.length) toast.info(`${r.data.equity_accounts_created.length} equity account(s) added to your chart of accounts`);
      if (r.data.conversion_journal) toast.info(`Equity transfer journal ${r.data.conversion_journal.reference} posted (${r.data.conversion_journal.amount.toLocaleString()})`, { duration: 8000 });
      onChanged?.(r.data);
      onOpenChange(false);
      setPassword(''); setForm(''); setReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change the legal form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="legal-form-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#283F8B]" /> Change legal form</DialogTitle>
          <DialogDescription>Current: <span className="font-medium text-foreground">{current?.legal_form_label || '—'}</span></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-800" data-testid="legal-form-change-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              This changes how your profits are taxed, which tax return the platform prepares, the equity section of your balance sheet and your filing deadlines.
              Tax computations for fiscal years ending on or after the effective date will follow the new form. The change is recorded permanently in the audit trail.
            </p>
          </div>
          <LegalFormPicker options={options} value={form} onChange={setForm} testId="legal-form-change-select" className="h-11" />
          {regimeChanges && (
            <p className="text-xs text-violet-700 bg-violet-500/10 border border-violet-500/20 rounded-md p-2" data-testid="legal-form-regime-change-note">
              Tax regime changes from <b>{currentRegime.replace('_', ' ')}</b> to <b>{target.regime.replace('_', ' ')}</b>.
            </p>
          )}
          {target?.partners_required && <PartnersEditor partners={partners} onChange={setPartners} />}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="lf-effective">Effective from</Label>
              <Input id="lf-effective" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} data-testid="legal-form-effective-from" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lf-password">Your password</Label>
              <Input id="lf-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="legal-form-password" />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="lf-reason">Reason (optional)</Label>
            <Textarea id="lf-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Incorporated as a limited company on 1 July" data-testid="legal-form-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="legal-form-change-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form || !password} className="bg-[#283F8B] hover:bg-[#1f3270]" data-testid="legal-form-change-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
